import { VFXEffectConfig } from '../../VFXEffectRegistry';
import { glintScatter, ringBlast, starFlash } from './burstBuilders';
import { driftMotes } from './statusAuraBuilders';
import { PLAYER_HITBOX } from '../../../../shared/logic/CombatAnchors';
import { LANTERN_V2_WINDOW_OFFSETS } from '../../../../shared/assets/hushLantern';

const FEET_Y = PLAYER_HITBOX.height / 2;
const LANDING = { offsetX: 0, offsetY: FEET_Y } as const;

/** Warm palette for Hush Nook / Lanternskip (warm core, sand gold, cloth blue accent). */
const HUSH_FX = {
  warmCore: 0xffe3a0,
  paperLight: 0xf0deb0,
  paperBase: 0xd7bc83,
  clothBlue: 0x6c9bae,
  outline: 0x2b4145,
} as const;

/** Lanternskip travel origin burst — small heel bead light and soft warm motes. */
const LANTERNSKIP_TRAVEL: VFXEffectConfig = {
  id: 'lanternskip_travel',
  name: 'Lanternskip Travel',
  category: 'burst',
  description: 'Warm heel light and skipping motes along the Lanternskip leap',
  primitives: [
    ringBlast({
      radius: 12,
      color: HUSH_FX.warmCore,
      alpha: 0.75,
      glow: true,
      depth: 95,
      stroke: { color: HUSH_FX.paperLight, width: 1.5 },
      expandTo: 2.0,
      durationMs: 200,
    }),
    starFlash({
      points: 4,
      innerRadius: 2,
      outerRadius: 10,
      color: HUSH_FX.warmCore,
      alpha: 0.9,
      glow: true,
      depth: 96,
      durationMs: 160,
    }),
    glintScatter({
      count: 6,
      spread: 22,
      color: HUSH_FX.paperBase,
      radius: 1.8,
      durationMs: 220,
    }),
  ],
};

/** Lanternskip landing — soft contact mark at the feet with tiny warm flecks. */
const LANTERNSKIP_LANDING: VFXEffectConfig = {
  id: 'lanternskip_landing',
  name: 'Lanternskip Landing',
  category: 'burst',
  description: 'Soft skipping contact mark upon landing from Lanternskip',
  primitives: [
    {
      type: 'ellipse',
      width: 20,
      height: 5,
      ...LANDING,
      color: HUSH_FX.warmCore,
      alpha: 0.8,
      depth: 93,
      glow: true,
      tween: { alpha: 0, scale: 1.5, duration: 220, ease: 'Power2' },
    },
    {
      type: 'ellipse',
      width: 10,
      height: 3,
      ...LANDING,
      color: HUSH_FX.clothBlue,
      alpha: 0.6,
      depth: 94,
      tween: { alpha: 0, scale: 1.8, duration: 180, ease: 'Power2' },
    },
    glintScatter({
      count: 4,
      spread: 16,
      color: HUSH_FX.paperLight,
      radius: 1.5,
      durationMs: 200,
    }),
  ],
};

/** Lanternskip learn lesson cue — gentle warm ring and rising beads upon acquiring the trick. */
const LANTERNSKIP_LEARN: VFXEffectConfig = {
  id: 'lanternskip_learn',
  name: 'Lanternskip Lesson',
  duration: 600,
  category: 'burst',
  description: 'Warm lesson glow when Mallow teaches Lanternskip',
  primitives: [
    ringBlast({
      radius: 20,
      color: HUSH_FX.warmCore,
      alpha: 0.85,
      glow: true,
      depth: 95,
      stroke: { color: HUSH_FX.paperLight, width: 2 },
      expandTo: 2.2,
      durationMs: 450,
    }),
    driftMotes({
      direction: 'up',
      count: 8,
      spread: 26,
      radius: 2.2,
      color: HUSH_FX.warmCore,
      alpha: 0.9,
      glow: true,
      travel: 32,
      duration: 500,
    }),
  ],
};

/** Window centers of lantern v2 relative to its light core (the portal's
 * `lightCoreAnchor`), taken from the generator so art and wink cannot drift. */
const HUSH_WINDOWS = LANTERN_V2_WINDOW_OFFSETS;

function hushWink(still: boolean): VFXEffectConfig['primitives'] {
  const scale = (moving: number) => (still ? 1 : moving);
  return [
    // A soft warm halo blooms behind the paper, then settles.
    { ...ringBlast({ radius: 24, expandTo: scale(1.3), color: HUSH_FX.warmCore, alpha: 0.4, glow: true,
      depth: 95, durationMs: 1150, ease: 'Sine.easeOut' }) },
    // The three windows wink in reading order: upper-left, upper-right, lower.
    ...HUSH_WINDOWS.map((w, i) => ({
      ...ringBlast({ radius: w.r + 1.5, expandTo: scale(1.7), color: i === 1 ? HUSH_FX.paperLight : HUSH_FX.warmCore,
        alpha: 1, glow: true, depth: 96, durationMs: 560, delayMs: i * 300 }),
      offsetX: w.x, offsetY: w.y,
    })),
    ...(still ? [] : [driftMotes({
      direction: 'up', count: 3, spread: 12, color: HUSH_FX.paperLight, alpha: 0.55,
      radius: 1.5, duration: 1200, travel: 10,
    })]),
  ];
}

/** Hush entrance wink — a finite discovery cue at the hung lantern: a soft warm
 * halo and the three windows winking in turn (1.2s). */
const HUSH_ENTRANCE_WINK: VFXEffectConfig = {
  id: 'hush_entrance_wink',
  name: 'Hush Entrance Wink',
  duration: 1200,
  category: 'burst',
  description: 'Warm halo and three window winks in reading order at the hung three-window lantern',
  primitives: hushWink(false),
};

/** Reduced-motion variant: the same light, fading in place (no growth, no motes). */
const HUSH_ENTRANCE_WINK_STILL: VFXEffectConfig = {
  id: 'hush_entrance_wink_still',
  name: 'Hush Entrance Wink (still)',
  duration: 1200,
  category: 'burst',
  description: 'Reduced-motion wink: warm halo and window lights fade in place',
  primitives: hushWink(true),
};

/** A hideout light (window or door leak) swelling once with the lantern wink. */
function lightSwell(still: boolean): VFXEffectConfig['primitives'] {
  return [
    { ...ringBlast({ radius: 30, expandTo: still ? 1 : 1.5, color: HUSH_FX.warmCore, alpha: 0.42, glow: true,
      depth: 94, durationMs: 1300, delayMs: 150, ease: 'Sine.easeOut' }) },
  ];
}

const HUSH_LIGHT_SWELL: VFXEffectConfig = {
  id: 'hush_light_swell',
  name: 'Hush Light Swell',
  duration: 1450,
  category: 'burst',
  description: 'Warm light leaking from the hideout window or door swells once with the lantern wink',
  primitives: lightSwell(false),
};

const HUSH_LIGHT_SWELL_STILL: VFXEffectConfig = {
  id: 'hush_light_swell_still',
  name: 'Hush Light Swell (still)',
  duration: 1450,
  category: 'burst',
  description: 'Reduced-motion light swell: the leak brightens and fades in place',
  primitives: lightSwell(true),
};

export const LANTERNSKIP_VFX: VFXEffectConfig[] = [
  LANTERNSKIP_TRAVEL,
  LANTERNSKIP_LANDING,
  LANTERNSKIP_LEARN,
  HUSH_ENTRANCE_WINK,
  HUSH_ENTRANCE_WINK_STILL,
  HUSH_LIGHT_SWELL,
  HUSH_LIGHT_SWELL_STILL,
];
